import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { ReactNode, useState } from 'react';

interface ConfirmationOptions {
  title: string;
  message: ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export function useConfirmation() {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions>({
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
  });
  const [resolveRef, setResolveRef] = useState<
    ((value: boolean) => void) | null
  >(null);

  const confirm = (
    customOptions?: Partial<ConfirmationOptions>,
  ): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setOptions({
        ...options,
        ...customOptions,
      });
      setResolveRef(() => resolve);
      setIsOpen(true);
    });
  };

  const handleConfirm = () => {
    if (resolveRef) {
      resolveRef(true);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (resolveRef) {
      resolveRef(false);
    }
    setIsOpen(false);
  };

  const ConfirmationDialog = (): ReactNode => (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      title={options.title}
      centered
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
    >
      <Stack gap="md">
        <Text>{options.message}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={handleCancel}>
            {options.cancelButtonText}
          </Button>
          <Button color="red" onClick={handleConfirm}>
            {options.confirmButtonText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  return {
    confirm,
    ConfirmationDialog,
  };
}
